import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MarketingNav, MarketingFooter } from '../components/marketing/MarketingChrome';
import './LandingPage.css';
import './MarketingPage.css';

/**
 * /pricing — the second mid-funnel commercial-intent page.
 *
 * "Is it free" and "how much does X cost" are among the highest-intent queries
 * in this category, and a searcher asking them is one step from signing up. The
 * site previously had no page to answer either, so that traffic had nowhere to
 * land.
 *
 * Every claim here is verified against the codebase: there is no billing
 * integration anywhere in the repo (no Stripe, no subscription state, no
 * paywall), so "free" is a statement of fact rather than a promotional offer.
 * If a paid tier is ever added, this page and its Offer schema must change
 * FIRST — a stale price in structured data is a trust problem, not a copy nit.
 */

const SITE_URL = 'https://letscoparent.us';
const CANONICAL = `${SITE_URL}/pricing`;
const OG_IMAGE = `${SITE_URL}/og-default.png`;

const META_TITLE = 'Pricing — coparent is Free for Both Parents';
const META_DESCRIPTION =
  'coparent is free. Shared custody calendar, expense splitting, activities, documents, and messaging for both parents — no subscription, no per-parent fee, no paywalled schedule.';

const INCLUDED = [
  'Shared custody calendar with every built-in pattern, plus custom cycles',
  'Day swaps and trip blocks, with approval from the other parent',
  'Expense tracking with 50/50 or custom splits and a running balance',
  'Recurring expenses and spending insights by category and child',
  'Activities, chores, and household rules across both homes',
  'Document storage for agreements, insurance cards, and school forms',
  'A message thread scoped to logistics',
  'Push notifications and handoff reminders',
  'Accounts for both parents, and for household members like a grandparent or nanny',
  'iPhone, iPad, and any browser',
];

const FAQS = [
  {
    q: 'Is coparent really free?',
    a: 'Yes. There is no subscription, no trial that expires, and no paid tier. Every feature described on this site is available to both parents at no cost.',
  },
  {
    q: 'Do you charge per parent?',
    a: 'No. Both parents create their own free account, and household members you invite — a grandparent who does Tuesday pickup, for example — are free as well.',
  },
  {
    q: 'Is any part of the schedule behind a paywall?',
    a: 'No. The custody calendar, swaps, and trips are the core of the product, and gating them would defeat the point. Nothing about the schedule is restricted.',
  },
  {
    q: 'How do you make money, then?',
    a: 'We do not yet. coparent is early, and the priority right now is making the product genuinely useful for the families using it. If that ever changes, we will say so here first and we will not take away something you already rely on.',
  },
  {
    q: 'Do you sell my data?',
    a: 'No. Your family’s information is visible only to the parents in your family. We do not share it, sell it, or use it for advertising.',
  },
  {
    q: 'What happens to my data if I stop using it?',
    a: 'You can delete your account from Settings, which removes your data. Until you do, your schedule and history stay where you left them.',
  },
];

const PricingPage = () => {
  const navigate = useNavigate();

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'coparent',
    applicationCategory: 'LifestyleApplication',
    operatingSystem: 'iOS, Web',
    description: META_DESCRIPTION,
    url: CANONICAL,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      url: CANONICAL,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Pricing', item: CANONICAL },
    ],
  };

  return (
    <div className="landing-page">
      <Helmet>
        <title>{META_TITLE}</title>
        <meta name="description" content={META_DESCRIPTION} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:title" content={META_TITLE} />
        <meta property="og:description" content={META_DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={META_TITLE} />
        <meta name="twitter:description" content={META_DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <MarketingNav />

      <header className="mk-hero">
        <div className="lp-container">
          <p className="mk-eyebrow">Pricing</p>
          <h1 className="mk-h1">
            It’s free.<br />
            <em>For both of you.</em>
          </h1>
          <p className="mk-lede">
            No subscription, no per-parent fee, and nothing about your custody
            schedule locked behind an upgrade prompt.
          </p>
        </div>
      </header>

      <main>
        <section className="mk-section">
          <div className="lp-container">
            <div className="mk-price-card">
              <p className="mk-eyebrow">Everything, for everyone</p>
              <p className="mk-price">
                $0<span className="mk-price-unit">/month</span>
              </p>
              <p className="mk-body">
                Both parents. Every feature. However many kids you have.
              </p>
              <button className="lp-btn-primary" onClick={() => navigate('/register')}>
                Create a free account
              </button>
              <ul className="mk-list mk-list-tight">
                {INCLUDED.map((item) => (
                  <li key={item}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="mk-section mk-section-alt">
          <div className="lp-container">
            <h2 className="mk-h2 mk-h2-centered">Questions about cost</h2>
            <div className="mk-faq">
              {FAQS.map((f) => (
                <details key={f.q} className="mk-faq-item">
                  <summary>{f.q}</summary>
                  <p>{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="lp-final-cta">
          <div className="lp-container">
            <div className="lp-final-cta-card">
              <h2 className="mk-h2">See what’s in it first.</h2>
              <p className="mk-body">
                The <Link to="/features">full feature list</Link> covers how the
                calendar, swaps, and expense splitting actually work.
              </p>
              <button className="lp-btn-primary" onClick={() => navigate('/register')}>
                Get started free
              </button>
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
};

export default PricingPage;
